import { useEffect, useState } from "react"
import useStore from "./useStore"
import { SETTINGS_CATEGORIES } from "../components/settingsComponents/config/SettingsCategories";
import { SETTINGS_CONFIG } from "../components/settingsComponents/config/SettingsConfig";
import useLoading from "./useLoading";
import { GlobalSettingsService } from "../services/strategies/settingsService/GlobalSettingsService";
import { StoreSettingsService } from "../services/strategies/settingsService/StoreSettingsService";

const settingsDictionary = {
    'all': 'Active at all stores',
    'list': 'Active at selected stores',
    'none': 'Inactive at all stores'
}

function createSettingsService(context) {
    switch (context) {
        case "store":
            return new StoreSettingsService();
        case "global":
            return new GlobalSettingsService();
        default:
            throw new Error(`Invalid settings context: ${context}`);
    }
}

// This helper computes the store setting status (all, none, or some)
const computeSettingStatus = (stores, propertyName) => {
    const enabledCount = stores.filter((store) => store[propertyName]).length;

    if (enabledCount === stores.length) {
        return settingsDictionary.all;
    } else if (enabledCount === 0) {
        return settingsDictionary.none;
    }
    return settingsDictionary.list;
};

// Merge store and global settings into a unified data structure.
const mergeSettings = (stores, globalData) => {
    // Process store settings
    const storeSettings = Object.entries(SETTINGS_CATEGORIES).map(
        ([categoryKey, category]) => ({
            category: category.label,
            data: category.settings
                .filter((settingKey) => SETTINGS_CONFIG.store[settingKey])
                .map((settingKey) => {
                    const configItem = SETTINGS_CONFIG.store[settingKey];
                    return {
                        label: configItem.label || settingKey.replace(/_/g, " "),
                        // Use computeSettingStatus to decide the current value for store settings.
                        currentValue: computeSettingStatus(stores, configItem.propertyName),
                        context: 'store',
                        ...configItem,
                    };
                }),
        })
    ).filter((setting)=> setting.data.length > 0);

    // Process global settings
    const globalSettings = Object.entries(SETTINGS_CATEGORIES).map(
        ([categoryKey, category]) => ({
            category: category.label,
            data: category.settings
                .filter((settingKey) => SETTINGS_CONFIG.global[settingKey])
                .map((settingKey) => {
                    const configItem = SETTINGS_CONFIG.global[settingKey];
                    return {
                        label: configItem.label || settingKey.replace(/_/g, " "),
                        key: settingKey, // include a key to later identify the setting
                        value: globalData[settingKey] || "Not Configured",
                        context: 'global',
                        ...configItem,
                    };
                }),
        })
    ).filter((setting)=> setting.data.length > 0);

    const mergedSettings = [...storeSettings, ...globalSettings].reduce((acc, current) => {
        const existingCategory = acc.find(item => item.category === current.category);
        if (existingCategory) {
            existingCategory.data = [...existingCategory.data, ...current.data];
        } else {
            acc.push(current);
        }
        return acc;
    }, []);
    
    return mergedSettings;
};

const useSettings = () => {
    const { unfilteredStores, setUnfilteredStores } = useStore();
    const [ settingSelected, setSettingSelected ] = useState({});
    const [ settingsData, setSettingsData ] = useState([]);
    const { setIsLoading } = useLoading()

    // Instantiate both service strategies
    const storeService = createSettingsService("store");
    const globalService = createSettingsService("global");

    useEffect(() => {
        const fetchAllSettings = async () => {
            try {
                // Fetch all types concurrently.
                setIsLoading(true);
                const [globalResponse] = await Promise.all([
                    globalService.getSettings(),
                ]);
                setIsLoading(false);
                
                const aggregatedSettings = mergeSettings(unfilteredStores, globalResponse);
                setSettingsData(aggregatedSettings);
            } catch (error) {
                console.error("Error fetching settings:", error);
            }
        };
        fetchAllSettings();
    }, []);

    const updateSetting = async (value) => {
        if (!settingSelected.context) return;
        try {
            const settingService = settingSelected.context == 'store' ? storeService : globalService;
            
            const params = { settingSelected, value }
            
            setIsLoading(true)
            const { settingValue } = await settingService.updateSetting(params);
            setIsLoading(false)

            //Updating local state to reflect the new value.
            const updatedSettings = settingService.mapResponseToLocalValue(
                { settingsData, settingSelected, settingsDictionary },
                settingValue
            )
            if (updatedSettings){
                setSettingsData(updatedSettings);
                const stores = await storeService.getSettings()
                setUnfilteredStores(stores);
            }
        }catch(error) {
            console.error("Error updating setting:", error);
            setIsLoading(false)
            throw error;
        }
    };

    return { settingsData, settingSelected, setSettingSelected, updateSetting }
} 

export default useSettings;